import React, { useState, useEffect, useMemo } from 'react';
import { walletAPI, shopAPI, farmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  WalletIcon,
  BanknotesIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

const ITEMS_PER_PAGE = 10;

const Wallet = () => {
  const { user } = useAuth();
  const accountType = user?.accountType || 'farmer';
  const canWithdraw = accountType !== 'client';
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [escrows, setEscrows] = useState([]);
  
  // Farm state (only for farm owners)
  const [farmWallet, setFarmWallet] = useState(null);
  const [farmId, setFarmId] = useState(null);
  const [farmOwner, setFarmOwner] = useState(null);
  const [isFarmOwner, setIsFarmOwner] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [txPage, setTxPage] = useState(1);
  const [txFilter, setTxFilter] = useState({ category: '', type: '' });
  
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '' });
  const [withdrawing, setWithdrawing] = useState(false);
  const [settlementSettings, setSettlementSettings] = useState(null);

  // Top-up state
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpForm, setTopUpForm] = useState({ amount: '', phone: '' });
  const [toppingUp, setToppingUp] = useState(false);
  const [topUpPending, setTopUpPending] = useState(null); // { transactionId, checkoutRequestId }

  // Theme based on account type
  const theme = {
    farmer: { gradient: 'from-green-600 to-emerald-600', shadow: 'shadow-green-500/25', light: 'bg-green-50', text: 'text-green-600' },
    supplier: { gradient: 'from-blue-600 to-indigo-600', shadow: 'shadow-blue-500/25', light: 'bg-blue-50', text: 'text-blue-600' },
    client: { gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-500/25', light: 'bg-amber-50', text: 'text-amber-600' }
  }[accountType] || { gradient: 'from-green-600 to-emerald-600', shadow: 'shadow-green-500/25', light: 'bg-green-50', text: 'text-green-600' };

  useEffect(() => {
    loadWalletData();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTransactions();
    } else if (activeTab === 'escrows') {
      loadEscrows();
    } else if (activeTab === 'withdrawals') {
      loadWithdrawals();
    }
  }, [activeTab, txPage, txFilter]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const res = await walletAPI.getWallet();
      setWallet(res.data.wallet);
      setSummary(res.data.summary);
      setSettlementSettings(res.data.settlementSettings);
      
      // For farmers who are farm owners, also load farm wallet
      if (accountType === 'farmer') {
        try {
          const farmsRes = await farmAPI.getAll();
          const farms = farmsRes.data.farms || farmsRes.data || [];
          if (farms.length > 0) {
            const farm = farms[0];
            setFarmId(farm.id);
            const isOwner = farm.currentUserRole === 'owner';
            setIsFarmOwner(isOwner);
            
            // Only load farm wallet for owners
            if (isOwner) {
              const farmWalletRes = await shopAPI.getFarmWallet(farm.id);
              setFarmWallet(farmWalletRes.data.wallet);
              setFarmOwner(farmWalletRes.data.owner);
            }
          }
        } catch (err) {
          console.error('Failed to load farm data:', err?.response?.data || err?.message);
        }
      }
    } catch (error) {
      console.error('Load wallet error:', error);
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const params = { page: txPage, limit: ITEMS_PER_PAGE, ...txFilter };
      const res = await walletAPI.getTransactions(params);
      setTransactions(res.data.transactions || []);
      setTxTotal(res.data.total || 0);
    } catch (error) {
      console.error('Load transactions error:', error);
    }
  };

  const loadEscrows = async () => {
    try {
      const res = await walletAPI.getEscrows();
      setEscrows(res.data.escrows || []);
    } catch (error) {
      console.error('Load escrows error:', error);
    }
  };

  const loadWithdrawals = async () => {
    try {
      const res = await walletAPI.getWithdrawals();
      setWithdrawals(res.data.withdrawals || []);
    } catch (error) {
      console.error('Load withdrawals error:', error);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!settlementSettings) {
      toast.error('Please configure your settlement settings first');
      return;
    }
    if (Number(withdrawForm.amount) < 500) {
      toast.error('Minimum withdrawal is KES 500');
      return;
    }
    if (Number(withdrawForm.amount) > wallet?.availableBalance) {
      toast.error('Insufficient balance');
      return;
    }
    setWithdrawing(true);
    try {
      // Use settlement settings from shop/supplier profile
      // Only include relevant fields based on withdrawal method
      const withdrawalData = {
        amount: withdrawForm.amount,
        withdrawalMethod: settlementSettings.method
      };
      
      if (settlementSettings.method === 'mpesa') {
        withdrawalData.mpesaPhone = settlementSettings.mpesaPhone;
      } else if (settlementSettings.method === 'bank') {
        withdrawalData.bankName = settlementSettings.bankName;
        withdrawalData.bankAccountNumber = settlementSettings.bankAccountNumber;
        withdrawalData.bankAccountName = settlementSettings.bankAccountName || '';
      }
      
      await walletAPI.requestWithdrawal(withdrawalData);
      toast.success('Withdrawal request submitted');
      setShowWithdraw(false);
      setWithdrawForm({ amount: '' });
      loadWalletData();
      if (activeTab === 'withdrawals') loadWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const requestCancelWithdrawal = async (id) => {
    const reason = window.prompt('Reason for cancellation request (optional):');
    if (reason === null) return; // User cancelled prompt
    try {
      await walletAPI.requestCancelWithdrawal(id, { reason });
      toast.success('Cancellation request submitted. Awaiting admin approval.');
      loadWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to request cancellation');
    }
  };

  // Handle top-up via M-Pesa STK Push
  const handleTopUp = async (e) => {
    e.preventDefault();
    const amount = Number(topUpForm.amount);
    if (amount < 10) {
      toast.error('Minimum top-up amount is KES 10');
      return;
    }
    if (amount > 150000) {
      toast.error('Maximum top-up amount is KES 150,000');
      return;
    }
    if (!topUpForm.phone) {
      toast.error('Please enter your M-Pesa phone number');
      return;
    }

    setToppingUp(true);
    try {
      const res = await walletAPI.initiateTopUp({
        amount: amount,
        phone: topUpForm.phone
      });
      
      toast.success('Payment request sent to your phone');
      setTopUpPending({
        transactionId: res.data.transactionId,
        checkoutRequestId: res.data.checkoutRequestId
      });
      
      // Start polling for payment status
      pollTopUpStatus(res.data.transactionId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setToppingUp(false);
    }
  };

  // Poll for top-up status
  const pollTopUpStatus = async (transactionId) => {
    let attempts = 0;
    const maxAttempts = 30; // 60 seconds max
    
    const checkStatus = async () => {
      try {
        const res = await walletAPI.checkTopUpStatus(transactionId);
        
        if (res.data.status === 'completed') {
          toast.success(`Top-up successful! KES ${res.data.amount.toLocaleString()} added to your wallet`);
          setShowTopUp(false);
          setTopUpForm({ amount: '', phone: '' });
          setTopUpPending(null);
          loadWalletData();
          if (activeTab === 'transactions') loadTransactions();
          return;
        } else if (res.data.status === 'failed') {
          toast.error('Payment failed or was cancelled');
          setTopUpPending(null);
          return;
        }
        
        // Still pending, continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          toast.error('Payment timeout. Please check your M-Pesa messages');
          setTopUpPending(null);
        }
      } catch (error) {
        console.error('Error checking top-up status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        }
      }
    };
    
    // Start polling after 3 seconds
    setTimeout(checkStatus, 3000);
  };

  const txTotalPages = Math.ceil(txTotal / ITEMS_PER_PAGE);

  const getCategoryLabel = (category) => {
    const labels = {
      top_up: 'Top Up', withdrawal_request: 'Withdrawal', withdrawal_completed: 'Withdrawal Complete',
      withdrawal_cancelled: 'Withdrawal Cancelled', order_payment: 'Order Payment', order_escrow: 'Escrow Hold',
      escrow_release: 'Escrow Release', escrow_refund: 'Escrow Refund', order_received: 'Payment Received',
      refund: 'Refund', subscription: 'Subscription', platform_fee: 'Platform Fee', promo_credit: 'Promo Credit', adjustment: 'Adjustment'
    };
    return labels[category] || category;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner /></div>;
  }

  const tabs = ['overview', 'transactions', 'escrows', ...(canWithdraw ? ['withdrawals'] : [])];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`relative overflow-hidden bg-gradient-to-br ${theme.gradient} rounded-2xl p-6 sm:p-8 text-white`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <WalletIcon className="h-8 w-8" />
            </div>
            <div>
              <p className="text-white/80 text-sm">Available Balance</p>
              <p className="text-3xl sm:text-4xl font-bold">KES {Number(wallet?.availableBalance || 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTopUp(true)}
              className="inline-flex items-center px-5 py-2.5 bg-white/20 backdrop-blur text-white border border-white/30 rounded-xl hover:bg-white/30 transition-colors font-medium"
            >
              <ArrowDownIcon className="h-5 w-5 mr-2" />
              Top Up
            </button>
            {canWithdraw && (
              <button
                onClick={() => setShowWithdraw(true)}
                disabled={!wallet || wallet.availableBalance < 500}
                className="inline-flex items-center px-5 py-2.5 bg-white text-gray-900 rounded-xl hover:bg-white/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUpIcon className="h-5 w-5 mr-2" />
                Withdraw
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Farm Owner Indicator - only shown to farm owners */}
      {accountType === 'farmer' && farmId && isFarmOwner && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <BuildingStorefrontIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">
              <span className="font-medium">You are the farm owner.</span> All shop earnings are credited to your wallet above.
            </p>
          </div>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500">Total Balance</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 mt-0.5 sm:mt-1 truncate">
                KES {Number(wallet?.totalBalance || wallet?.availableBalance + wallet?.escrowBalance || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-2 sm:p-2.5 bg-gray-100 rounded-lg sm:rounded-xl flex-shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500">In Escrow</p>
              <p className="text-base sm:text-xl font-bold text-amber-600 mt-0.5 sm:mt-1 truncate">
                KES {Number(wallet?.escrowBalance || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-2 sm:p-2.5 bg-amber-50 rounded-lg sm:rounded-xl flex-shrink-0">
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
            </div>
          </div>
        </div>

        {canWithdraw && (
          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500">Pending Withdrawal</p>
                <p className="text-base sm:text-xl font-bold text-blue-600 mt-0.5 sm:mt-1 truncate">
                  KES {Number(wallet?.pendingWithdrawal || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-2 sm:p-2.5 bg-blue-50 rounded-lg sm:rounded-xl flex-shrink-0">
                <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
            </div>
          </div>
        )}

        {summary?.incomingEscrow > 0 && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 sm:p-5 border border-green-100">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-green-700">Incoming</p>
                <p className="text-base sm:text-xl font-bold text-green-700 mt-0.5 sm:mt-1 truncate">
                  KES {Number(summary.incomingEscrow).toLocaleString()}
                </p>
              </div>
              <div className="p-2 sm:p-2.5 bg-green-100 rounded-lg sm:rounded-xl flex-shrink-0">
                <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 overflow-x-auto">
          <nav className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? `${theme.text} border-b-2 border-current bg-gray-50`
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
              <div className={`${theme.light} rounded-xl p-4 sm:p-6`}>
                <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Quick Summary</h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between items-center p-2.5 sm:p-3 bg-white rounded-lg">
                    <span className="text-gray-600 text-sm">Escrows (Buyer)</span>
                    <span className="font-semibold text-sm">{summary?.pendingEscrowsAsBuyer || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 sm:p-3 bg-white rounded-lg">
                    <span className="text-gray-600 text-sm">Escrows (Seller)</span>
                    <span className="font-semibold text-sm">{summary?.pendingEscrowsAsSeller || 0}</span>
                  </div>
                  {canWithdraw && (
                    <div className="flex justify-between items-center p-2.5 sm:p-3 bg-white rounded-lg">
                      <span className="text-gray-600 text-sm">Pending Withdrawals</span>
                      <span className="font-semibold text-sm">{summary?.pendingWithdrawals || 0}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">How Wallet Works</h3>
                <div className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm">
                  <div className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                    <p className="text-gray-600"><strong>Top Up:</strong> Add funds via M-Pesa</p>
                  </div>
                  <div className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                    <p className="text-gray-600"><strong>Pay:</strong> Use balance for secure payments</p>
                  </div>
                  <div className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                    <p className="text-gray-600"><strong>Escrow:</strong> Funds held until delivery confirmed</p>
                  </div>
                  {canWithdraw && (
                    <div className="flex items-start gap-2.5 sm:gap-3">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</div>
                      <p className="text-gray-600"><strong>Withdraw:</strong> Request withdrawal (min KES 500)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 sm:gap-3 items-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <select
                  className="px-2 sm:px-3 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-green-500"
                  value={txFilter.type}
                  onChange={(e) => { setTxFilter({ ...txFilter, type: e.target.value }); setTxPage(1); }}
                >
                  <option value="">All Types</option>
                  <option value="credit">Credits (In)</option>
                  <option value="debit">Debits (Out)</option>
                </select>
                <select
                  className="px-2 sm:px-3 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-green-500"
                  value={txFilter.category}
                  onChange={(e) => { setTxFilter({ ...txFilter, category: e.target.value }); setTxPage(1); }}
                >
                  <option value="">All Categories</option>
                  <option value="top_up">Top Up</option>
                  <option value="order_payment">Order Payment</option>
                  <option value="escrow_release">Escrow Release</option>
                  <option value="withdrawal_request">Withdrawal</option>
                </select>
                <button onClick={() => { setTxFilter({ category: '', type: '' }); setTxPage(1); }} className={`text-xs sm:text-sm ${theme.text} hover:underline`}>Clear</button>
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {transactions.length === 0 ? (
                      <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">No transactions yet</td></tr>
                    ) : (
                      transactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(tx.createdAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs rounded-full ${tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {tx.type === 'credit' ? <ArrowDownIcon className="h-3 w-3 mr-1" /> : <ArrowUpIcon className="h-3 w-3 mr-1" />}
                              {getCategoryLabel(tx.category)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{tx.description || '-'}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold text-right ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.type === 'credit' ? '+' : '-'}KES {Number(tx.amount).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">KES {Number(tx.balanceAfter).toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="sm:hidden space-y-3">
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">No transactions yet</div>
                ) : (
                  transactions.map((tx) => (
                    <div key={tx.id} className="bg-white rounded-xl border border-gray-200 p-3.5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {tx.type === 'credit' ? <ArrowDownIcon className="h-3 w-3 mr-1" /> : <ArrowUpIcon className="h-3 w-3 mr-1" />}
                          {getCategoryLabel(tx.category)}
                        </span>
                        <span className="text-xs text-gray-500">{new Date(tx.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">{tx.description || '-'}</p>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-bold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.type === 'credit' ? '+' : '-'}KES {Number(tx.amount).toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-500">Bal: KES {Number(tx.balanceAfter).toLocaleString()}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {txTotalPages > 1 && (
                <div className="flex items-center justify-between pt-2 sm:pt-4">
                  <p className="text-xs sm:text-sm text-gray-500">Page {txPage} of {txTotalPages}</p>
                  <div className="flex gap-1.5 sm:gap-2">
                    <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1} className="p-1.5 sm:p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"><ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" /></button>
                    <button onClick={() => setTxPage(p => Math.min(txTotalPages, p + 1))} disabled={txPage === txTotalPages} className="p-1.5 sm:p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"><ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" /></button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Escrows Tab */}
          {activeTab === 'escrows' && (
            <div className="space-y-3 sm:space-y-4">
              {escrows.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <ShieldCheckIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                  <p className="font-medium text-gray-900 text-sm sm:text-base">No active escrows</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">Escrows appear when you pay for orders with wallet</p>
                </div>
              ) : (
                <div className="grid gap-3 sm:gap-4">
                  {escrows.map((escrow) => (
                    <div key={escrow.id} className="bg-gray-50 rounded-xl p-3.5 sm:p-5 border border-gray-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{escrow.orderType === 'ShopOrder' ? 'Farm Order' : 'Supplier Order'} #{escrow.orderId?.slice(0, 8)}</p>
                          <p className="text-xs sm:text-sm text-gray-500">{escrow.userRole === 'buyer' ? 'You are the buyer' : 'You are the seller'}</p>
                        </div>
                        <span className={`px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm rounded-full flex-shrink-0 ${escrow.status === 'holding' ? 'bg-amber-100 text-amber-700' : escrow.status === 'released' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {escrow.status.charAt(0).toUpperCase() + escrow.status.slice(1)}
                        </span>
                      </div>
                      <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-4">
                        <div><p className="text-xs text-gray-500">Total</p><p className="font-semibold text-xs sm:text-base">KES {Number(escrow.totalAmount).toLocaleString()}</p></div>
                        <div><p className="text-xs text-gray-500">Seller Gets</p><p className="font-semibold text-green-600 text-xs sm:text-base">KES {Number(escrow.sellerAmount).toLocaleString()}</p></div>
                        <div><p className="text-xs text-gray-500">Platform Fee</p><p className="font-semibold text-gray-600 text-xs sm:text-base">KES {Number(escrow.platformFee).toLocaleString()}</p></div>
                      </div>
                      {escrow.autoReleaseAt && escrow.status === 'holding' && (
                        <p className="mt-2 sm:mt-3 text-xs text-gray-500">Auto-release: {new Date(escrow.autoReleaseAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Withdrawals Tab */}
          {activeTab === 'withdrawals' && (
            <div className="space-y-3 sm:space-y-4">
              {withdrawals.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <BanknotesIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                  <p className="font-medium text-gray-900 text-sm sm:text-base">No withdrawal requests</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">Request a withdrawal to see it here</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Request #</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {withdrawals.map((wd) => (
                          <tr key={wd.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{wd.requestNumber}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(wd.createdAt).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">KES {Number(wd.amount).toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{wd.withdrawalMethod === 'mpesa' ? 'M-Pesa' : 'Bank'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2.5 py-1 text-xs rounded-full ${
                                wd.status === 'completed' ? 'bg-green-100 text-green-700' : 
                                wd.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                                wd.status === 'processing' ? 'bg-blue-100 text-blue-700' : 
                                wd.status === 'cancel_requested' ? 'bg-purple-100 text-purple-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {wd.status === 'cancel_requested' ? 'Cancel Pending' : wd.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {wd.status === 'pending' && (<button onClick={() => requestCancelWithdrawal(wd.id)} className="text-red-600 hover:text-red-700 text-sm font-medium">Request Cancel</button>)}
                              {wd.status === 'cancel_requested' && (<span className="text-xs text-gray-500">Awaiting approval</span>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-3">
                    {withdrawals.map((wd) => (
                      <div key={wd.id} className="bg-white rounded-xl border border-gray-200 p-3.5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <p className="text-xs text-gray-500">#{wd.requestNumber}</p>
                            <p className="font-bold text-gray-900">KES {Number(wd.amount).toLocaleString()}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            wd.status === 'completed' ? 'bg-green-100 text-green-700' : 
                            wd.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                            wd.status === 'processing' ? 'bg-blue-100 text-blue-700' : 
                            wd.status === 'cancel_requested' ? 'bg-purple-100 text-purple-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {wd.status === 'cancel_requested' ? 'Cancel Pending' : wd.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{wd.withdrawalMethod === 'mpesa' ? 'M-Pesa' : 'Bank'}</span>
                          <span>{new Date(wd.createdAt).toLocaleDateString()}</span>
                        </div>
                        {wd.status === 'pending' && (
                          <button onClick={() => requestCancelWithdrawal(wd.id)} className="mt-2 text-red-600 hover:text-red-700 text-xs font-medium">Request Cancel</button>
                        )}
                        {wd.status === 'cancel_requested' && (
                          <p className="mt-2 text-xs text-purple-600">Awaiting admin approval</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className={`p-4 sm:p-6 bg-gradient-to-r ${theme.gradient} text-white`}>
              <h3 className="text-lg sm:text-xl font-bold">Request Withdrawal</h3>
              <p className="text-white/80 text-xs sm:text-sm mt-1">Minimum KES 500 • Available: KES {Number(wallet?.availableBalance || 0).toLocaleString()}</p>
            </div>
            
            {!settlementSettings ? (
              <div className="p-4 sm:p-6">
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Settlement Settings Required</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Please configure your payment settings in your {accountType === 'supplier' ? 'supplier profile' : 'shop settings'} before requesting a withdrawal.
                  </p>
                  <button onClick={() => setShowWithdraw(false)} className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm">Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleWithdraw} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* Destination Info - Read Only */}
                <div className={`p-3 sm:p-4 rounded-xl ${theme.light} border`}>
                  <p className="text-xs font-medium text-gray-500 mb-2">Withdrawal Destination</p>
                  {settlementSettings.method === 'mpesa' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📱</span>
                      <div>
                        <p className="font-semibold text-gray-900">M-Pesa</p>
                        <p className="text-sm text-gray-600">{settlementSettings.mpesaPhone || 'Not configured'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏦</span>
                      <div>
                        <p className="font-semibold text-gray-900">{settlementSettings.bankName || 'Bank Transfer'}</p>
                        <p className="text-sm text-gray-600">A/C: {settlementSettings.bankAccountNumber || 'Not configured'}</p>
                        {settlementSettings.bankAccountName && <p className="text-xs text-gray-500">{settlementSettings.bankAccountName}</p>}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    To change, update your {accountType === 'supplier' ? 'supplier settings' : 'shop settings'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Amount (KES)</label>
                  <input type="number" min="500" max={wallet?.availableBalance || 0} required className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} placeholder="Enter amount" />
                </div>

                <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button type="button" onClick={() => setShowWithdraw(false)} className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm">Cancel</button>
                  <button type="submit" disabled={withdrawing || !settlementSettings.mpesaPhone && !settlementSettings.bankAccountNumber} className={`flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r ${theme.gradient} text-white rounded-xl hover:shadow-lg ${theme.shadow} transition-all font-medium text-sm disabled:opacity-50`}>{withdrawing ? 'Submitting...' : 'Request Withdrawal'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Top Up Modal */}
      {showTopUp && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className={`p-4 sm:p-6 bg-gradient-to-r ${theme.gradient} text-white`}>
              <h3 className="text-lg sm:text-xl font-bold">Top Up Wallet</h3>
              <p className="text-white/80 text-xs sm:text-sm mt-1">Add funds via M-Pesa • Min KES 10</p>
            </div>
            <form onSubmit={handleTopUp} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {topUpPending ? (
                <div className="text-center py-6 sm:py-8">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-gray-200 border-t-green-600 mx-auto mb-3 sm:mb-4"></div>
                  <p className="text-gray-700 font-medium text-sm sm:text-base">Waiting for M-Pesa confirmation...</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-2">Please enter your M-Pesa PIN on your phone</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Amount (KES)</label>
                    <input 
                      type="number" 
                      min="10" 
                      max="150000" 
                      required 
                      placeholder="Enter amount"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500" 
                      value={topUpForm.amount} 
                      onChange={(e) => setTopUpForm({ ...topUpForm, amount: e.target.value })} 
                    />
                    <p className="text-xs text-gray-500 mt-1">Min: KES 10 • Max: KES 150,000</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">M-Pesa Phone Number</label>
                    <input 
                      type="tel" 
                      required 
                      placeholder="07XXXXXXXX or 254XXXXXXXXX"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500" 
                      value={topUpForm.phone} 
                      onChange={(e) => setTopUpForm({ ...topUpForm, phone: e.target.value })} 
                    />
                    <p className="text-xs text-gray-500 mt-1">You'll receive an STK push on this number</p>
                  </div>
                  
                  {/* Quick amount buttons */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Quick Select</label>
                    <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
                      {[500, 1000, 2000, 5000, 10000].map(amt => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setTopUpForm({ ...topUpForm, amount: String(amt) })}
                          className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                            topUpForm.amount === String(amt) 
                              ? `bg-gradient-to-r ${theme.gradient} text-white` 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {amt >= 1000 ? `${amt/1000}K` : amt}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowTopUp(false); setTopUpPending(null); setTopUpForm({ amount: '', phone: '' }); }} 
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm"
                >
                  Cancel
                </button>
                {!topUpPending && (
                  <button 
                    type="submit" 
                    disabled={toppingUp} 
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r ${theme.gradient} text-white rounded-xl hover:shadow-lg ${theme.shadow} transition-all font-medium text-sm disabled:opacity-50`}
                  >
                    {toppingUp ? 'Sending...' : 'Pay via M-Pesa'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
